import { useCallback, useEffect, useRef, useState } from 'react';

import { CameraOutlined, CheckCircleOutlined, LoadingOutlined, WarningOutlined } from '@ant-design/icons';
import { Button, Spin } from 'antd';

import { Alert } from '@/components/Alert';
import { useStepScope } from '@/components/Step/scope';
import { trpcVanillaClient } from '@/lib/trpc';

import type { FaceTecFaceScanProcessor, FaceTecSessionResult, InitializedSDK } from './facetec-sdk';
import { getInitializedSDK } from './facetec-sdk';

function fetchSessionToken(sessionId: string | undefined) {
  if (sessionId) {
    return trpcVanillaClient.facetec.mobileSessionToken.query({ sessionId });
  }

  return trpcVanillaClient.facetec.sessionToken.query();
}

function callLiveness(sessionId: string | undefined, sessionResult: FaceTecSessionResult) {
  if (sessionId) {
    return trpcVanillaClient.facetec.mobileLiveness.mutate({
      sessionId,
      faceScan: sessionResult.faceScan,
      auditTrailImage: sessionResult.auditTrail[0],
      lowQualityAuditTrailImage: sessionResult.lowQualityAuditTrail[0],
      facetecSessionId: sessionResult.sessionId
    });
  }

  return trpcVanillaClient.facetec.liveness.mutate({
    faceScan: sessionResult.faceScan,
    auditTrailImage: sessionResult.auditTrail[0],
    lowQualityAuditTrailImage: sessionResult.lowQualityAuditTrail[0],
    sessionId: sessionResult.sessionId
  });
}

type Status = 'idle' | 'initializing' | 'processing' | 'success' | 'error';

type FaceInputProps = {
  onSuccess: (result: { faceId: string }) => void;
  sessionId?: string;
};

export const FaceInput = ({ onSuccess, sessionId }: FaceInputProps) => {
  const scopeId = useStepScope();
  const [status, setStatus] = useState<Status>('idle');
  const [errorMessage, setErrorMessage] = useState('');
  const onSuccessRef = useRef(onSuccess);
  const busyRef = useRef(false);

  useEffect(() => {
    onSuccessRef.current = onSuccess;
  }, [onSuccess]);

  const startLivenessSession = useCallback(async () => {
    if (busyRef.current) {
      return;
    }

    busyRef.current = true;

    setStatus('initializing');
    setErrorMessage('');

    let sdk: InitializedSDK;

    try {
      sdk = await getInitializedSDK(sessionId);
    } catch (err) {
      console.error('[FaceTec] SDK init failed:', err);
      busyRef.current = false;
      setStatus('error');
      setErrorMessage('Não foi possível inicializar o reconhecimento facial. Verifique sua conexão e tente novamente.');
      return;
    }

    setStatus('processing');

    try {
      const { sessionToken } = await fetchSessionToken(sessionId);

      const processor: FaceTecFaceScanProcessor = {
        processSessionResultWhileFaceTecSDKWaits(sessionResult, faceScanResultCallback) {
          if (sessionResult.status !== sdk.FaceTecSDK.FaceTecSessionStatus.SessionCompletedSuccessfully) {
            faceScanResultCallback.cancel();
            return;
          }

          (async () => {
            let result;

            try {
              result = await callLiveness(sessionId, sessionResult);
            } catch {
              faceScanResultCallback.cancel();
              setStatus('error');
              setErrorMessage('Erro ao processar reconhecimento facial. Tente novamente.');
              return;
            }

            if (result.scanResultBlob) {
              faceScanResultCallback.proceedToNextStep(result.scanResultBlob);
            } else {
              faceScanResultCallback.cancel();
            }

            const isValid = result.valid && result.protocol;

            if (isValid) {
              setStatus('success');
              onSuccessRef.current({ faceId: result.protocol as string });
            } else {
              setStatus('error');
              const fallbackMessage = 'Validação facial não aprovada. Tente novamente.';
              setErrorMessage(result.cause ?? fallbackMessage);
            }
          })();
        },

        onFaceTecSDKCompletelyDone() {
          busyRef.current = false;
          setStatus(prev => (prev === 'processing' ? 'idle' : prev));
        }
      };

      new sdk.FaceTecSession(processor, sessionToken);
    } catch (err) {
      console.error('[FaceTec] Session start failed:', err);
      busyRef.current = false;
      setStatus('error');
      setErrorMessage('Não foi possível iniciar a sessão. Tente novamente.');
    }
  }, [sessionId]);

  if (status === 'initializing') {
    return (
      <div className='flex flex-col items-center justify-center gap-4 py-12'>
        <Spin indicator={<LoadingOutlined spin className='text-3xl' />} />
        <p className='text-sm text-gray-500'>Inicializando reconhecimento facial...</p>
      </div>
    );
  }

  if (status === 'error') {
    return (
      <div className='flex flex-col items-center gap-4'>
        <Alert
          type='error'
          icon={WarningOutlined}
          title='Erro no reconhecimento facial'
          description={errorMessage}
          className='w-full'
        />
        <Button
          id={scopeId('face-retry-button')}
          type='primary'
          icon={<CameraOutlined />}
          onClick={startLivenessSession}
        >
          Tentar novamente
        </Button>
      </div>
    );
  }

  if (status === 'success') {
    return (
      <div className='flex flex-col items-center gap-3 py-8'>
        <CheckCircleOutlined className='text-4xl text-green-500' />
        <p className='text-sm font-medium text-green-700'>Reconhecimento facial concluído com sucesso!</p>
      </div>
    );
  }

  if (status === 'processing') {
    return (
      <div className='flex flex-col items-center justify-center gap-4 py-12'>
        <Spin indicator={<LoadingOutlined spin className='text-3xl' />} />
        <p className='text-sm text-gray-500'>Processando reconhecimento facial...</p>
      </div>
    );
  }

  return (
    <div className='flex flex-col items-center justify-center'>
      <Button id={scopeId('liveness-button')} type='primary' icon={<CameraOutlined />} onClick={startLivenessSession}>
        Iniciar reconhecimento facial
      </Button>
    </div>
  );
};
